// React
import { ReactElement, useEffect, useState } from 'react';
// Next
import { useTranslation } from 'next-i18next';
// Utils
import { getColorStyle, LightenDarkenColor } from '@utmb/utils/colors';
// DESIGN SYSTEM
import { Slides, SlidesItem, SlideProps } from '@utmb/design-system/slides/Slides';
import WorldSeriesStat from '@utmb/features/world-series/race-list/WorldSeriesStat';
import { Font } from '@utmb/design-system/text/font';
import { Image } from '@utmb/design-system/image/Image';
import { Link } from '@utmb/design-system/link/link';
import DotIcon from './DotIcon';
// INTERFACES
import SearchRaceView from '@utmb/types/view/SearchRaceView';
import { RaceRegistrationStatusEnum } from '@utmb/types/enum/RaceRegistrationStatusEnum';
// CSS
import styles from './my-wishlist-swipe-cards.module.scss';
import { ColorTypesEnum } from '@utmb/types/colors';
import { WorldSeriesRaceFollow } from '../world-series/follow/WorldSeriesRaceFollow';
import { useDashboard } from '@utmb/contexts/DashboardContext';

interface MyWishlistSwipeCardsProps {
    races: SearchRaceView[];
}

export const MyWishlistSwipeCards = (props: MyWishlistSwipeCardsProps): ReactElement | null => {
    const { races } = props;
    const [containerWidth, setContainerWidth] = useState<number | undefined>(undefined);
    const [showSliderDotsCSS, setShowSliderDotsCSS] = useState(styles.dots);
    const { refreshMember } = useDashboard();

    const { t } = useTranslation();

    const slideProps: SlideProps = {
        className: styles.container,
        carouselOptions: {
            containScroll: 'trimSnaps',
            align: 'center',
            draggable: true,
            loop: false,
        },
        dotsClassName: showSliderDotsCSS,
    };

    const handleResize = () => {
        const containerWidth = races.length * (parseInt(styles.slideItemMaxWidth) + parseInt(styles.slideItemMarginRight));
        if (document && document.body.clientWidth < containerWidth) {
            setContainerWidth(undefined);
            setShowSliderDotsCSS([styles.dots, styles.showdots].join(' '));
        } else {
            setContainerWidth(containerWidth);
            setShowSliderDotsCSS(styles.dots);
        }
    };

    useEffect(() => {
        window.addEventListener('resize', handleResize, false);
        handleResize();
    }, []);
    if (!races || !races.length) return null;

    return (
        <div id="wishlist" className={styles.root}>
            <Slides {...slideProps} pagination isDarkPagination containerWidth={containerWidth}>
                {() => (
                    <>
                        {races.map((race: SearchRaceView, index: number) => {
                            const colorToUse = race.raceThemeIsDark ? race.raceTheme : LightenDarkenColor(race.raceTheme, -20);
                            return (
                                <SlidesItem key={index} className={styles.item}>
                                    <div className={styles.image}>
                                        <Image layout="fill" objectFit="cover" image={race.media} maxWidth={500} />
                                        {race.eventLogo ? <Image layout="fill" objectFit="contain" image={race.eventLogo} className={styles.logo} /> : null}
                                        <WorldSeriesRaceFollow
                                            raceId={race.id}
                                            className={styles.follow}
                                            onAfterUpdate={() => {
                                                setTimeout(refreshMember, 1000);
                                            }}
                                        />
                                        {race.raceStatus && (
                                            <div className={styles.status}>
                                                <Font.OxaniumBold mobile="17" desktop="19">
                                                    {race.raceStatus.open ? (
                                                        <>
                                                            <DotIcon color="green" /> {t(`racesList.registration_open`)}
                                                        </>
                                                    ) : race.raceStatus.status === RaceRegistrationStatusEnum.AVAILABLE_SOON ? (
                                                        <>
                                                            <DotIcon color="#fd4b01" /> {t(`racesList.available_soon`)}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <DotIcon color="red" /> {t(`racesList.${race.raceStatus.status}`)}
                                                        </>
                                                    )}
                                                </Font.OxaniumBold>
                                            </div>
                                        )}

                                        {race.startDate && (
                                            <div
                                                className={styles.date}
                                                style={{
                                                    backgroundColor: `${race.raceTheme}`,
                                                    color: race.raceThemeIsDark ? getColorStyle(ColorTypesEnum.WHITE) : getColorStyle(ColorTypesEnum.DEFAULT),
                                                }}
                                            >
                                                <Font.OxaniumRegular mobile="12" desktop="14" as="span">
                                                    {race.startDate}
                                                </Font.OxaniumRegular>
                                            </div>
                                        )}
                                    </div>
                                    <div className={styles.content} style={{ borderColor: `${race.raceTheme}` }}>
                                        <div className={styles.header}>
                                            {race.logo ? (
                                                <Image
                                                    layout="fixed"
                                                    objectFit="contain"
                                                    width={172}
                                                    height={50}
                                                    image={race.logo}
                                                    alt={race.name}
                                                    className={styles.logo}
                                                />
                                            ) : (
                                                <Font.OxaniumBold mobile="20" desktop="22" className="lh-25" as="span">
                                                    <span
                                                        style={{
                                                            ...(colorToUse && {
                                                                color: colorToUse,
                                                            }),
                                                        }}
                                                    >
                                                        {race.name}
                                                    </span>
                                                </Font.OxaniumBold>
                                            )}
                                        </div>
                                        <div className={styles.cols}>
                                            {race.details.statsUp.slice(0, 4).map((stat) => (
                                                <WorldSeriesStat
                                                    {...stat}
                                                    labelClassName={styles.statLabel}
                                                    valueClassName={styles.statValue}
                                                    key={stat.name}
                                                />
                                            ))}
                                            {/*race.details.statsDown.slice(0, 2).map((stat) => (
                                                <WorldSeriesStat
                                                    {...stat}
                                                    labelClassName={styles.statLabel}
                                                    valueClassName={styles.statValue}
                                                    key={stat.name}
                                                    className={styles.place}
                                                />
                                            ))*/}
                                        </div>
                                        <div className={styles.footer}>
                                            <Link.Button
                                                label={t('racesList.labelLinkDetail')}
                                                href={`${race.slug}`}
                                                color={getColorStyle(ColorTypesEnum.WHITE)}
                                                bgcolor={getColorStyle(ColorTypesEnum.PRIMARY)}
                                                className={styles.btn}
                                                underline={false}
                                            />
                                        </div>
                                    </div>
                                </SlidesItem>
                            );
                        })}
                    </>
                )}
            </Slides>
        </div>
    );
};
